import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

// Admin emails — add any emails that should get admin access
const ADMIN_EMAILS = [
  'alvarezjd404@gmail.com',
];

/**
 * Converts a Google credential JWT into our app user shape.
 */
function parseGoogleCredential(credential) {
  const decoded = jwtDecode(credential);
  return {
    uid: decoded.sub,
    displayName: decoded.name,
    email: decoded.email,
    photoURL: decoded.picture,
    isAdmin: ADMIN_EMAILS.includes(decoded.email),
    credential, // keep raw token for API calls if needed
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore persisted session on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('fvl_auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate the stored credential hasn't expired
        if (parsed.credential) {
          const decoded = jwtDecode(parsed.credential);
          if (decoded.exp * 1000 > Date.now()) {
            setUser(parsed);
          } else {
            localStorage.removeItem('fvl_auth');
          }
        }
      }
    } catch {
      localStorage.removeItem('fvl_auth');
    }
    setLoading(false);
  }, []);

  /**
   * Called by the Google sign-in button's onSuccess callback.
   * Receives a credentialResponse from @react-oauth/google.
   */
  const handleGoogleSuccess = useCallback((credentialResponse) => {
    const appUser = parseGoogleCredential(credentialResponse.credential);
    setUser(appUser);
    localStorage.setItem('fvl_auth', JSON.stringify(appUser));
  }, []);

  const signOut = useCallback(() => {
    setUser(null);
    localStorage.removeItem('fvl_auth');
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, handleGoogleSuccess, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
